import { getCartSectionsToRender, getCartSectionInnerHTML, updateCartLiveRegions } from 'utils';
import { publish } from 'lib/pubsub';
import { PUB_SUB_EVENTS } from 'lib/constants';
import { AddonCartService } from './addon-cart-service';

/**
 * AddonBase - Base class for all addon components
 * Provides common functionality for state management, cart operations, and lifecycle
 * 
 * Abstract class - must be extended by concrete implementations
 */
export class AddonBase extends HTMLElement {
  constructor() {
    super();

    this._state = {
      error: null
    };

    this._domRefs = new Map();
    this._addonService = new AddonCartService();
    this._eventListeners = [];

    this._initialized = false;
  }

  connectedCallback() {
    try {
      this._initializeComponent();
    } catch (error) {
      this._handleError('Failed to initialize component', error);
    }
  }

  disconnectedCallback() {
    this._cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (this._initialized && oldValue !== newValue) {
      this._handleAttributeChange(name, newValue);
    }
  }

  // =============================================================================
  // INITIALIZATION METHODS (Template Method Pattern)
  // =============================================================================

  /**
   * Initialize component - template method that calls abstract methods
   * @protected
   */
  _initializeComponent() {
    // Prevent re-initialization if already initialized
    if (this._initialized) {
      return;
    }

    // Only initialize if connected to the main document
    // This prevents initialization when element is just in a DocumentFragment
    if (!this.isConnected || this.getRootNode() !== document) {
      return;
    }

    this._validateAttributes();
    this._cacheDOMReferences();
    this._initializeState();
    this._setupEventListeners();
    this._setupAccessibility();
    this._render();
    this._initialized = true;
  }


  /**
   * Initialize component state with default state support
   * @protected
   */
  _initializeState() {
    const defaultState = this._parseDefaultState();

    // Merge with child class state
    this._state = {
      ...this._getInitialState(),
      ...defaultState
    };
  }


  // =============================================================================
  // ABSTRACT METHODS (Must be implemented by child classes)
  // =============================================================================

  /**
   * Get initial state for the component
   * @abstract
   * @protected
   * @returns {Object} Initial state object
   */
  _getInitialState() {
    throw new Error('_getInitialState() must be implemented by child class');
  }

  /**
   * Validate required attributes
   * @abstract
   * @protected
   */
  _validateAttributes() {
    throw new Error('_validateAttributes() must be implemented by child class');
  }

  /**
   * Cache DOM references
   * @abstract
   * @protected
   */
  _cacheDOMReferences() {
    throw new Error('_cacheDOMReferences() must be implemented by child class');
  }

  /**
   * Setup event listeners
   * @abstract
   * @protected
   */
  _setupEventListeners() {
    throw new Error('_setupEventListeners() must be implemented by child class');
  }

  /**
   * Setup accessibility attributes
   * @abstract
   * @protected
   */
  _setupAccessibility() {
    throw new Error('_setupAccessibility() must be implemented by child class');
  }

  /**
   * Render component based on current state
   * @abstract
   * @protected
   */
  _render() {
    throw new Error('_render() must be implemented by child class');
  }

  /**
   * Handle attribute changes
   * @abstract
   * @protected
   * @param {string} name - Attribute name
   * @param {string} newValue - New attribute value
   */
  _handleAttributeChange(name, newValue) {
    throw new Error('_handleAttributeChange() must be implemented by child class');
  }

  // =============================================================================
  // STATE MANAGEMENT (Common implementation)
  // =============================================================================

  /**
   * Parse default state from attribute
   * @protected
   * @returns {Object} Parsed default state
   */
  _parseDefaultState() {
    const defaultStateAttr = this.getAttribute('default-state');

    if (!defaultStateAttr) {
      return {};
    }

    try {
      const cleanedJson = defaultStateAttr.trim().replace(/\s+/g, ' ');
      const parsed = JSON.parse(cleanedJson);

      if (typeof parsed !== 'object' || parsed === null) {
        console.warn(`${this.constructor.name}: default-state must be an object`);
        return {};
      }

      // Get allowed keys from child class
      const allowedKeys = this._getAllowedStateKeys();
      const validState = {};

      allowedKeys.forEach(key => {
        if (key in parsed) {
          validState[key] = this._validateStateValue(key, parsed[key]);
        }
      });

      return validState;
    } catch (error) {
      console.error(`${this.constructor.name}: Failed to parse default-state attribute`, error);
      return {};
    }
  }

  /**
   * Get allowed state keys - can be overridden by child classes
   * @protected
   * @returns {string[]} Array of allowed state keys
   */
  _getAllowedStateKeys() {
    return ['error'];
  }

  /**
   * Validate state value - can be overridden by child classes
   * @protected
   * @param {string} key - State key
   * @param {*} value - Value to validate
   * @returns {*} Validated value
   */
  _validateStateValue(key, value) {
    // Default validation for boolean states
    if (key.startsWith('is')) {
      return Boolean(value);
    }
    return value;
  }

  /**
   * Update component state and trigger re-render
   * @protected
   * @param {Object} newState - State updates
   */
  _updateState(newState, render = true) {
    if (!this._state) return;

    const hasChanges = Object.keys(newState).some(key => this._state[key] !== newState[key]);

    if (hasChanges) {
      this._state = { ...this._state, ...newState };
      if (render) {
        this._render();
      }
    }
  }

  /**
   * Reinitialize state when default-state attribute changes
   * @protected
   */
  _reinitializeState() {
    const preservedState = this._getPreservedStateKeys();
    const preservedValues = {};

    // Preserve certain state values during reinitialization
    preservedState.forEach(key => {
      if (this._state && key in this._state) {
        preservedValues[key] = this._state[key];
      }
    });

    const defaultState = this._parseDefaultState();

    this._state = {
      ...this._getInitialState(),
      ...preservedValues,
      ...defaultState
    };

    this._render();
  }

  /**
   * Get state keys to preserve during reinitialization
   * @protected
   * @returns {string[]} Array of state keys to preserve
   */
  _getPreservedStateKeys() {
    return ['error'];
  }

  // =============================================================================
  // EVENT HANDLING (Common implementation)
  // =============================================================================

  /**
   * Dispatch custom events
   * @protected
   * @param {string} eventName - Event name
   * @param {Object} detail - Event detail object
   */
  _dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, {
      detail: { ...detail, component: this },
      bubbles: true,
      cancelable: true
    });

    this.dispatchEvent(event);
  }

  /**
   * Add event listener with cleanup tracking
   * @protected
   * @param {Element} element - Element to add listener to
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   */
  _addEventListener(element, event, handler) {
    element.addEventListener(event, handler);
    this._eventListeners.push({ element, event, handler });
  }

  // =============================================================================
  // CART OPERATIONS (Common implementation)
  // =============================================================================

  /**
   * Execute cart operations
   * @protected
   * @param {Array} operations - Cart operations to execute
   * @returns {Promise} Promise that resolves with cart response
   */
  async _executeCartOperations(operations) {
    return new Promise((resolve, reject) => {
      this._addonService.executeCartOperations(operations, (response) => {
        try {
          this._handleCartResponse(response);
          resolve(response);
        } catch (error) {
          reject(error);
        }
      });
    });
  }

  /**
   * Handle cart API response
   * @protected
   * @param {string} state - Cart state JSON string
   */
  _handleCartResponse(state) {
    const parsedState = JSON.parse(state);

    if (parsedState.errors) {
      updateCartLiveRegions(0, parsedState.errors);
      throw new Error('Cart update failed: ' + JSON.stringify(parsedState.errors));
    }

    // Update cart sections
    getCartSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id)?.querySelector(section.selector) ||
        document.getElementById(section.id);

      if (elementToReplace) {
        elementToReplace.innerHTML = getCartSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      }
    });

    // Publish cart update event
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: this.constructor.name.toLowerCase(),
      cartData: parsedState
    });
  }

  // =============================================================================
  // ERROR HANDLING (Common implementation)
  // =============================================================================

  /**
   * Handle errors with user feedback
   * @protected
   * @param {string} message - Error message
   * @param {Error} error - Error object
   */
  _handleError(message, error) {
    console.error(`${this.constructor.name}: ${message}`, error);

    this._updateState({
      error: message
    });

    this._dispatchEvent(this.constructor.CONFIG.EVENTS.ERROR, {
      message,
      error
    });

    // Clear error after timeout
    setTimeout(() => {
      if (this._state && this._state.error === message) {
        this._updateState({ error: null });
      }
    }, this.constructor.CONFIG?.TIMEOUTS?.ERROR_FEEDBACK || 5000);
  }

  // =============================================================================
  // CLEANUP (Common implementation)
  // =============================================================================

  /**
   * Clean up event listeners and references
   * @protected
   */
  _cleanup() {
    // Clean up event listeners
    if (this._eventListeners) {
      this._eventListeners.forEach(({ element, event, handler }) => {
        element.removeEventListener(event, handler);
      });
      this._eventListeners = [];
    }

    // Clear DOM references
    if (this._domRefs) {
      this._domRefs.clear();
    }

    // Reset state
    this._state = null;
    this._initialized = false;

    // Child-specific cleanup
    if (this._performChildCleanup) {
      this._performChildCleanup();
    }
  }

  // =============================================================================
  // UTILITY METHODS (Common implementation)
  // =============================================================================

  /**
   * Get component name for logging/debugging
   * @protected
   * @returns {string} Component name
   */
  _getComponentName() {
    return this.constructor.name;
  }

  /**
   * Check if component is initialized
   * @protected
   * @returns {boolean} Whether component is initialized
   */
  _isInitialized() {
    return this._initialized;
  }

  /**
   * Safely get DOM reference
   * @protected
   * @param {string} key - Reference key
   * @returns {Element|null} DOM element or null
   */
  _getDOMRef(key) {
    return this._domRefs.get(key) || null;
  }

  /**
   * Store DOM reference
   * @protected
   * @param {string} key - Reference key
   * @param {Element} element - DOM element
   */
  _setDOMRef(key, element) {
    if (element) {
      this._domRefs.set(key, element);
    }
  }

  // =============================================================================
  // PUBLIC API (Common getters)
  // =============================================================================

  /**
   * Get current error state
   * @returns {string|null} Current error message
   */
  get error() {
    return this._state?.error || null;
  }

  /**
   * Check if component has error
   * @returns {boolean} Whether component has error
   */
  get hasError() {
    return !!this._state?.error;
  }

  /**
   * Get component initialization state
   * @returns {boolean} Whether component is initialized
   */
  get isInitialized() {
    return this._initialized;
  }
}